import { useEffect, useRef } from 'react';
import { FullProject, Task } from '../../types';
import { db } from '../../firebase';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { areProjectIdsMatching, isCommProject } from '../../utils/idNormalizer';
import { ensureInboxAndSampleProjectsSeeded } from './projectBootstrapSeeders';

export interface UseProjectBootstrapProps {
  userId?: string;
  userEmail?: string | null;
  tenantId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  activeWorkspace: 'professional' | 'private';
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  currentProjectIdRef: React.MutableRefObject<string>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  setCurrentProjectId: (id: string) => void;
  setLastSynced: React.Dispatch<React.SetStateAction<number | null>>;
  syncProjectToFirestore: (project: FullProject, isSilent?: boolean) => Promise<number | null>;
  fetchProjectsFromFirestore: () => Promise<FullProject[] | null>;
}

export function useProjectBootstrap({
  userId,
  userEmail,
  tenantId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  activeWorkspace,
  allProjectsRef,
  currentProjectIdRef,
  setAllProjects,
  setCurrentProjectId,
  setLastSynced,
  syncProjectToFirestore,
  fetchProjectsFromFirestore
}: UseProjectBootstrapProps) {
  const loadedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    if (loadedUserIdRef.current === userId) return;
    loadedUserIdRef.current = userId;

    const init = async () => {
      const [
        dbProjects,
        {
          DEFAULT_INBOX_ITEMS,
          DEFAULT_PROCESSED_ITEMS,
          getSampleContacts,
          getSecondarySampleProject,
          getSampleDataForBranch,
        },
      ] = await Promise.all([
        fetchProjectsFromFirestore(),
        import('../../utils/projectTemplates'),
      ]);
      let sourceProjects: FullProject[] = [];

      // 1. Fetch Local Server Projects (optional – only available when a local SQLite
      //    server is running alongside the app; silently ignored otherwise)
      try {
        const response = await fetch('/api/projects');
        const contentType = response.headers.get('content-type') || '';
        if (response.ok && contentType.includes('application/json')) {
          sourceProjects = await response.json();
        }
      } catch (e) {
        console.warn('[Init] Local server not available, skipping local projects fetch.');
      }

      // 2. Combine and filter zombies
      let initialList = [...(dbProjects || [])];
      const cloudIds = new Set(initialList.map(p => p.id));
      const zombiesToPurge: string[] = [];
      
      sourceProjects.forEach(sp => {
        // Skip projects belonging to another user UNLESS explicitly shared with current user
        const isSharedWithMe = !!(
          userEmail &&
          sp.sharedWith?.some((e: string) => e.toLowerCase().trim() === userEmail.toLowerCase().trim())
        );
        if (sp.userId && sp.userId !== userId && !isSharedWithMe) {
          return;
        }

        if (!cloudIds.has(sp.id)) {
          // CHECK: Is this a likely zombie? (Empty default project not present in cloud)
          const isEmpty = (sp.tasks || []).length === 0;
          const isDefaultName = sp.name === "Neues Projekt" || sp.name === "Unbenanntes Projekt";
          const isOld = sp.lastModified && (Date.now() - sp.lastModified > 1000 * 60); // > 1 minute old

          if (isEmpty && isDefaultName && isOld) {
            zombiesToPurge.push(sp.id);
          } else {
            initialList.push(sp);
          }
        }
      });

      // Async purge zombies from local SQLite
      if (zombiesToPurge.length > 0) {
        zombiesToPurge.forEach(id => {
          fetch(`/api/projects/${id}`, { method: 'DELETE' }).catch(() => {});
        });
      }

      // Migration logic for __comm__
      const legacyComm = initialList.find(p => p.id === '__comm__');
      if (legacyComm) {
        initialList = initialList.filter(p => p.id !== '__comm__');
        
        const existingComm = initialList.find(p => p.id === COMM_PROJECT_ID);
        if (existingComm) {
          const mergedTasks = [...(existingComm.tasks || [])];
          (legacyComm.tasks || []).forEach(lt => {
            if (!mergedTasks.find(mt => mt.id === lt.id)) mergedTasks.push(lt);
          });
          existingComm.tasks = mergedTasks;
          existingComm.lastModified = Math.max(existingComm.lastModified || 0, legacyComm.lastModified || 0);
        } else {
          initialList.push({ ...legacyComm, id: COMM_PROJECT_ID });
        }
        
        // Silently try to delete the reserved ID from Firestore
        if (userId !== 'e2e_local_uid') {
          try {
            deleteDoc(doc(db, "projects", "__comm__")).catch(() => {});
          } catch (e) {}
        }
      }

      let finalProjects: FullProject[] = initialList;

      // 4. Ensure Communication Project exists
      let commProject = finalProjects.find(p => p.id === COMM_PROJECT_ID || p.id === 'comm_internal' || areProjectIdsMatching(p.id, COMM_PROJECT_ID));
      const formatDateLocal = (offsetDays: number) => {
        const d = new Date();
        d.setDate(d.getDate() + offsetDays);
        return d.toISOString().split('T')[0];
      };
      const sampleContacts = getSampleContacts(userId, tenantId, COMM_PROJECT_ID, formatDateLocal);

      // Check dismissal flags across localStorage and project metadata
      const localSampleDismissed = typeof localStorage !== 'undefined' && localStorage.getItem('gwantt_sample_dismissed') === 'true';
      const commSampleDismissed = commProject?.metadata?.sampleDismissed === true || localSampleDismissed;

      if (!commProject) {
        commProject = {
          id: COMM_PROJECT_ID,
          name: "Kommunikation",
          tasks: commSampleDismissed ? [] : sampleContacts,
          lastModified: 1,
          color: 'bg-indigo-600',
          workspace: 'professional',
          userId: userId,
          tenantId: tenantId,
          metadata: commSampleDismissed ? { sampleDismissed: true } : {},
          changelog: []
        };
        finalProjects.push(commProject);
        syncProjectToFirestore(commProject);
        if (!commSampleDismissed && userId !== 'e2e_local_uid') {
          try {
            await Promise.all(sampleContacts.map(c => setDoc(doc(db, 'tasks', c.id.toString()), c)));
          } catch (e) {
            console.error('[Init] Failed to save sample contacts for comm project:', e);
          }
        }
      } else {
        // If commProject exists, ONLY seed sample contacts if the project has NO tasks at all AND sample data was not dismissed
        const existingTasks = commProject.tasks || [];
        if (existingTasks.length === 0 && !commSampleDismissed) {
          commProject.tasks = sampleContacts;
          if (userId !== 'e2e_local_uid') {
            try {
              await Promise.all(sampleContacts.map(c => setDoc(doc(db, 'tasks', c.id.toString()), c)));
            } catch (e) {
              console.error('[Init] Failed to save sample contacts for existing comm project:', e);
            }
          }
        }
      }

      // Auto-sanitize existing contacts: Strip legacy deprecated evaluation fields (socialCapital, mood)
      // WITHOUT overwriting user modifications or resetting names/notes
      if (commProject && commProject.tasks) {
        let hasSampleContactToClean = false;
        commProject.tasks = commProject.tasks.map(t => {
          if (t.socialCapital !== undefined || t.mood !== undefined) {
            hasSampleContactToClean = true;
            const updated: Task = { ...t };
            delete updated.socialCapital;
            delete updated.mood;
            return updated;
          }
          return t;
        });

        if (hasSampleContactToClean) {
          syncProjectToFirestore(commProject);
        }
      }

      // MIGRATION & SANITIZATION: Clean up orphaned sample contacts that may have leaked into normal projects
      // Note: Vorgänge (tasks with category === 'Kommunikation') belong to their respective projects and MUST NEVER be moved!
      finalProjects.forEach(proj => {
        if (!isCommProject(proj.id) && proj.id !== COMM_PROJECT_ID && proj.tasks && proj.tasks.length > 0) {
          const isLeakedContactRecord = (t: Task) =>
            String(t.id).startsWith('person_sample_contact_') ||
            (String(t.id).startsWith('sample_contact_') && t.type === 'contact');
          const orphanedSampleContacts = proj.tasks.filter(isLeakedContactRecord);
          if (orphanedSampleContacts.length > 0) {
            proj.tasks = proj.tasks.filter(t => !isLeakedContactRecord(t));
          }
        }
      });

      // 5 & 6. Ensure Inbox, Global Tenant Settings, and Sample Projects are seeded
      await ensureInboxAndSampleProjectsSeeded({
        finalProjects,
        userId,
        tenantId,
        INBOX_PROJECT_ID,
        COMM_PROJECT_ID,
        commSampleDismissed,
        DEFAULT_INBOX_ITEMS,
        DEFAULT_PROCESSED_ITEMS,
        getSampleDataForBranch,
        getSecondarySampleProject,
        syncProjectToFirestore,
      });

      // Merge with live onSnapshot listener state
      setAllProjects(prev => {
        const merged = [...finalProjects];
        prev.forEach(existingProj => {
          const idx = merged.findIndex(p => p.id === existingProj.id || areProjectIdsMatching(p.id, existingProj.id));
          if (idx !== -1) {
            const initTasks = merged[idx].tasks || [];
            const liveTasks = existingProj.tasks || [];
            const taskMap = new Map<string, Task>();
            // 1. Seed with initTasks
            initTasks.forEach(t => taskMap.set(String(t.id), t));
            // 2. Overlay with liveTasks: live tasks take precedence and user-created tasks are never lost
            liveTasks.forEach(t => {
              const existing = taskMap.get(String(t.id));
              if (!existing || (t.updatedAt || 0) >= (existing.updatedAt || 0)) {
                taskMap.set(String(t.id), { ...existing, ...t });
              }
            });
            merged[idx] = {
              ...merged[idx],
              ...existingProj,
              metadata: { ...(merged[idx].metadata || {}), ...(existingProj.metadata || {}) },
              tasks: Array.from(taskMap.values())
            };
          } else {
            merged.push(existingProj);
          }
        });
        return merged;
      });

      // Respect the already requested project ID (e.g. from URL or localStorage)
      let nextId = currentProjectIdRef.current;
      if (!nextId || nextId === 'default') {
        nextId = 'globalKanban';
      }
      
      currentProjectIdRef.current = nextId;
      setCurrentProjectId(nextId);
      setLastSynced(Date.now());
    };

    init();
  }, [
    userId,
    userEmail,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    activeWorkspace,
    syncProjectToFirestore,
    fetchProjectsFromFirestore,
    allProjectsRef,
    currentProjectIdRef,
    setAllProjects,
    setCurrentProjectId,
    setLastSynced
  ]);
}
